import React from 'react';
import { 
  CheckIcon, 
  TrashIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShoppingBagIcon,
  CreditCardIcon,
  StarIcon,
  MegaphoneIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { he } from 'date-fns/locale';

const NotificationDropdown = ({
  notifications,
  loading,
  unreadCount,
  onNotificationClick,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onClose
}) => {
  
  // Get icon for notification type
  const getNotificationIcon = (type, priority) => {
    const iconClass = `h-5 w-5 ${
      priority === 'urgent' ? 'text-red-500' :
      priority === 'high' ? 'text-orange-500' :
      priority === 'medium' ? 'text-blue-500' :
      'text-gray-500'
    }`;

    switch (type) {
      case 'order_created':
      case 'order_status_changed':
      case 'order_shipped':
      case 'order_delivered':
        return <ShoppingBagIcon className={iconClass} />;
      
      case 'payment_received':
      case 'payment_failed':
      case 'credit_limit_warning':
      case 'credit_limit_exceeded':
        return <CreditCardIcon className={iconClass} />;
      
      case 'inventory_low':
      case 'inventory_out':
        return <CubeIcon className={iconClass} />;
      
      case 'review_created':
        return <StarIcon className={iconClass} />;
      
      case 'promotion_created':
        return <MegaphoneIcon className={iconClass} />;
      
      case 'system_announcement':
      case 'account_update':
        return <InformationCircleIcon className={iconClass} />;
      
      default:
        return <InformationCircleIcon className={iconClass} />;
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'border-r-red-500 bg-red-50';
      case 'high':
        return 'border-r-orange-500 bg-orange-50';
      case 'medium':
        return 'border-r-blue-500 bg-blue-50';
      case 'low':
      default:
        return 'border-r-gray-300 bg-gray-50';
    }
  };

  // Format notification time
  const formatNotificationTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { 
        addSuffix: true, 
        locale: he 
      });
    } catch (error) {
      return 'זמן לא ידוע';
    }
  };

  return (
    <div className="absolute left-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            התראות
            {unreadCount > 0 && (
              <span className="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {unreadCount} חדשות
              </span>
            )}
          </h3>
          
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllAsRead}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              סמן הכל כנקרא
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <span className="mr-2 text-gray-600">טוען התראות...</span>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500">
            <InformationCircleIcon className="h-12 w-12 text-gray-300 mb-2" />
            <p className="text-sm">אין התראות חדשות</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`relative p-4 hover:bg-gray-50 cursor-pointer transition-colors border-r-4 ${
                  !notification.is_read ? getPriorityColor(notification.priority) : 'border-r-gray-200 bg-white'
                }`}
                onClick={() => onNotificationClick(notification)}
              >
                <div className="flex items-start space-x-3 space-x-reverse">
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.type, notification.priority)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${
                          !notification.is_read ? 'text-gray-900' : 'text-gray-600'
                        }`}>
                          {notification.title}
                        </p>
                        <p className={`text-sm mt-1 ${
                          !notification.is_read ? 'text-gray-700' : 'text-gray-500'
                        }`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          {formatNotificationTime(notification.sent_at)}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-1 space-x-reverse mr-2">
                        {!notification.is_read && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onMarkAsRead(notification.id);
                            }}
                            className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                            title="סמן כנקרא"
                          >
                            <CheckIcon className="h-4 w-4" />
                          </button>
                        )}
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(notification.id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="מחק התראה"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Important indicator */}
                    {notification.is_important && (
                      <div className="flex items-center mt-2">
                        <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 ml-1" />
                        <span className="text-xs text-amber-600 font-medium">חשוב</span>
                      </div>
                    )}

                    {/* Unread indicator */}
                    {!notification.is_read && (
                      <div className="absolute top-4 left-4">
                        <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => {
              // Navigate to full notifications page
              window.location.href = '/notifications';
              onClose();
            }}
            className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            צפה בכל ההתראות
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
